'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import Container from '@/components/ui/Container/Container';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type {
  PlantEntry,
  PlantImage,
  PlantTimelineDataItem,
  PlantsSectionWithHeading,
  PlantsVideoSection,
} from '@/types/plants';

/**
 * Extract the YouTube video id from a watch URL, short URL or embed URL.
 * Returns null if no id can be parsed — caller falls back to opening the
 * link in a new tab.
 */
function extractYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([a-z0-9_-]{6,})/i,
    /youtube(?:-nocookie)?\.com\/embed\/([a-z0-9_-]{6,})/i,
    /youtube\.com\/watch\?[^#]*[?&]?v=([a-z0-9_-]{6,})/i,
    /youtube\.com\/shorts\/([a-z0-9_-]{6,})/i,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function getOriginalMediaUrl(image: PlantImage | null | undefined): string | null {
  return resolveStrapiMediaUrl(image?.url);
}

function PinIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-red-500"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <h4 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/45">
      {children}
    </h4>
  );
}

interface VideoBlockProps {
  title: string;
  descriptionHtml: string;
  backgroundImage: PlantImage | null;
  button: PlantsVideoSection['button'];
}

/**
 * Hero-style video strip. Renders the CMS background image + title +
 * description until the user clicks Play, then swaps in an autoplaying
 * YouTube embed. If the CMS button URL isn't a YouTube link the play
 * button keeps its original external-link behavior.
 */
function VideoBlock({ title, descriptionHtml, backgroundImage, button }: VideoBlockProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const bgUrl = getOriginalMediaUrl(backgroundImage);
  const youtubeId = extractYouTubeId(button?.buttonUrl);
  const externalUrl = button?.buttonUrl ?? '';
  const isExternalLink = !youtubeId && /^https?:\/\//.test(externalUrl);
  const buttonText = button?.buttonText || 'Play Video';
  const ariaLabel = button?.ariaLabel || buttonText;

  return (
    <div className="relative w-full">
      <div className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 overflow-hidden">
        <div className="relative min-h-[min(60vh,440px)] w-full sm:min-h-[min(64vh,540px)] md:min-h-[560px]">
          {isPlaying && youtubeId ? (
            <>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                title={title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
              <button
                type="button"
                onClick={() => setIsPlaying(false)}
                aria-label="Close video"
                className="!absolute right-4 -top-2! !bg-red-600 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white shadow-lg backdrop-blur transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-white/80 sm:right-6 sm:top-9"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <CmsImage
                src={bgUrl}
                alt=""
                fill
                quality={90}
                className="object-cover object-center"
                sizes="100vw"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
              <div className="absolute inset-0 bg-black/55" aria-hidden />
              <div className="relative z-10 flex min-h-[min(60vh,440px)] flex-col items-center justify-center px-5 py-10 text-center sm:min-h-[min(64vh,540px)] sm:px-6 sm:py-12 md:min-h-[560px] md:px-10 md:py-14">
                <h3 className="max-w-[22ch] text-xl font-semibold leading-[1.15] tracking-tight text-white sm:text-2xl md:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                  {title}
                </h3>
                {descriptionHtml ? (
                  <div
                    className="mx-auto mt-4 max-w-xl text-sm leading-6 text-white/88 sm:mt-6 sm:leading-7 md:text-base md:leading-8 [&_p]:m-0"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                ) : null}
                {(() => {
                  if (youtubeId) {
                    return (
                      <button
                        type="button"
                        onClick={() => setIsPlaying(true)}
                        aria-label={ariaLabel}
                        className="mt-7 inline-flex items-center gap-3 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-lg transition hover:bg-white/95 sm:mt-8 sm:px-6 sm:py-3 md:mt-10 md:px-7"
                      >
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                          <svg
                            className="ml-0.5 h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                        {buttonText}
                      </button>
                    );
                  }
                  if (isExternalLink) {
                    return (
                      <a
                        href={externalUrl}
                        {...getCmsButtonLinkProps(button, '_blank')}
                        aria-label={ariaLabel}
                        className="mt-7 inline-flex items-center gap-3 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-lg transition hover:bg-white/95 sm:mt-8 sm:px-6 sm:py-3 md:mt-10 md:px-7"
                      >
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                          <svg
                            className="ml-0.5 h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                        {buttonText}
                      </a>
                    );
                  }
                  if (button) {
                    return (
                      <button
                        type="button"
                        className="mt-7 inline-flex items-center gap-3 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-lg transition hover:bg-white/95 sm:mt-8 sm:px-6 sm:py-3 md:mt-10 md:px-7"
                      >
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                          <svg
                            className="ml-0.5 h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                        {buttonText}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const TIMELINE_ACTIVE_COLOR = '#ffffff';
const TIMELINE_INACTIVE_DOT_COLOR = 'rgba(255,255,255,0.35)';
const TIMELINE_STEP_SEC = 5;
const TIMELINE_DOT_LEAD_SEC = 0.12;

/** Build the auto-advancing timeline that lights up the year dots + fill bar. */
function buildTimelineAnimation(
  itemsLength: number,
  hDots: (HTMLSpanElement | null)[],
  hFill: HTMLDivElement | null,
  activateStep: (index: number) => void,
): gsap.core.Timeline {
  const dots = hDots.filter(Boolean) as HTMLSpanElement[];

  const tl = gsap.timeline({
    repeat: -1,
    repeatDelay: 1.5,
    onRepeat: () => {
      activateStep(0);
    },
  });

  if (hFill) tl.set(hFill, { scaleX: 0, transformOrigin: '0% 50%' }, 0);
  dots.forEach((dot) => tl.set(dot, { backgroundColor: TIMELINE_INACTIVE_DOT_COLOR }, 0));

  const firstTarget = hDots[0];
  if (firstTarget) {
    tl.to(
      firstTarget,
      { backgroundColor: TIMELINE_ACTIVE_COLOR, duration: 0.25, ease: 'power2.out' },
      0,
    );
  }
  tl.call(() => activateStep(0), [], 0);

  const totalDuration = (itemsLength - 1) * TIMELINE_STEP_SEC;
  if (hFill) {
    tl.to(hFill, { scaleX: 1, duration: totalDuration, ease: 'none' }, 0);
  }

  for (let i = 1; i < itemsLength; i++) {
    const activateAt = i * TIMELINE_STEP_SEC - TIMELINE_DOT_LEAD_SEC;
    const dot = hDots[i];
    tl.call(() => activateStep(i), [], activateAt);
    if (dot) {
      tl.to(
        dot,
        { backgroundColor: TIMELINE_ACTIVE_COLOR, duration: 0.25, ease: 'power2.out' },
        activateAt,
      );
    }
  }

  return tl;
}

interface TimelineProps {
  heading: string;
  items: PlantTimelineDataItem[];
  onActiveChange?: (index: number) => void;
}

function Timeline({ heading, items, onActiveChange }: TimelineProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hFillRef = useRef<HTMLDivElement | null>(null);
  const hDotRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);

  hDotRefs.current.length = items.length;

  useEffect(() => {
    if (items.length <= 1) {
      setActiveIdx(0);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setActiveIdx(0);
      return;
    }

    let tl: gsap.core.Timeline | null = null;

    const activateStep = (index: number) => {
      setActiveIdx(index);
    };

    const start = () => {
      if (tl) return;
      tl = buildTimelineAnimation(items.length, hDotRefs.current, hFillRef.current, activateStep);
    };

    const stop = () => {
      if (tl) {
        tl.kill();
        tl = null;
      }
    };

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0.25 },
    );
    io.observe(node);

    return () => {
      io.disconnect();
      stop();
    };
  }, [items.length]);

  useEffect(() => {
    onActiveChange?.(activeIdx);
  }, [activeIdx, onActiveChange]);

  if (items.length === 0) return null;

  const leftRightPct = items.length > 1 ? 50 / items.length : 50;

  return (
    <div ref={containerRef} className="space-y-5 pt-2">
      <SectionLabel>{heading || 'THE TIMELINE OF EXCELLENCE'}</SectionLabel>

      <div className="space-y-4 overflow-x-auto [scrollbar-width:none] sm:space-y-5 [&::-webkit-scrollbar]:hidden">
        <div className="relative min-w-[min(100%,520px)]">
          <div
            className="pointer-events-none absolute inset-y-0 flex items-center"
            style={{ left: `${leftRightPct}%`, right: `${leftRightPct}%` }}
            aria-hidden
          >
            <div className="relative h-[2px] w-full">
              <div className="absolute inset-0 bg-white/20" />
              <div
                ref={hFillRef}
                className="absolute inset-0 bg-white"
                style={{ transform: 'scaleX(0)', transformOrigin: '0% 50%' }}
              />
            </div>
          </div>

          <div
            className="relative grid"
            style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
          >
            {items.map((t, i) => {
              const isCurrent = i === activeIdx;
              return (
                <div key={t.id} className="relative flex h-5 items-center justify-center">
                  <span
                    ref={(el) => {
                      hDotRefs.current[i] = el;
                    }}
                    className={`relative z-[1] h-2.5 w-2.5 rounded-full transition-shadow duration-300 ${
                      isCurrent ? 'shadow-[0_0_0_4px_rgba(255,255,255,0.18)]' : ''
                    }`}
                    style={{ backgroundColor: 'rgba(255,255,255,0.35)' }}
                    aria-hidden
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="grid min-w-[min(100%,520px)] gap-1 text-center text-[11px] leading-snug sm:gap-2 sm:text-[13px] md:text-sm"
          style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
        >
          {items.map((t, i) => {
            const isReached = i <= activeIdx;
            const isCurrent = i === activeIdx;
            let headingClass: string;
            if (!isReached) headingClass = 'font-medium text-white/40';
            else if (isCurrent) headingClass = 'font-semibold text-white';
            else headingClass = 'font-medium text-white';
            return (
              <div key={t.id} className="min-w-0 px-0.5 sm:px-1">
                <div className={`transition-colors duration-300 ${headingClass}`}>{t.heading}</div>
                <div
                  className={`mt-0.5 transition-colors duration-300 ${
                    isReached ? 'font-semibold text-white' : 'font-medium text-white/35'
                  }`}
                >
                  {t.subHeading || ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {items.some((item) => item.description) ? (
        <div className="grid border-t border-white/[0.08] pt-6 sm:pt-7 [&>*]:col-start-1 [&>*]:row-start-1">
          {items.map((item, i) =>
            item.description ? (
              <div
                key={item.id}
                className={`text-sm leading-6 text-white/65 sm:leading-7 [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-white transition-opacity duration-500 ${
                  i === activeIdx ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                aria-hidden={i !== activeIdx}
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  );
}

function ComingSoonPlantSection({
  section,
  plant,
}: {
  section: PlantsSectionWithHeading;
  plant: PlantEntry;
}) {
  const image = section.plantImageOptional;
  const imageUrl = getOriginalMediaUrl(image);

  return (
    <div className="bg-black text-white">
      <Container>
        <article className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0d0d0d] shadow-[0_0_0_1px_rgba(255,255,255,0.04)]">
          <div className="grid lg:grid-cols-2 lg:items-stretch">
            <div className="relative order-2 min-h-[220px] sm:min-h-[280px] lg:order-1 lg:min-h-[min(420px,55vh)]">
              <CmsImage
                src={imageUrl}
                alt={image?.alternativeText || plant.plantName || 'Coming soon plant'}
                fill
                quality={90}
                className="object-cover grayscale"
                sizes="(max-width: 1024px) 100vw, 50vw"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>
            <div className="order-1 flex flex-col items-center justify-center px-5 py-10 text-center sm:px-6 sm:py-12 md:px-12 md:py-14 lg:order-2 lg:text-left">
              {plant.locationName ? (
                <div className="flex items-center justify-center gap-2 text-sm text-red-500 lg:justify-start">
                  <PinIcon />
                  <span className="font-medium tracking-wide">{plant.locationName}</span>
                </div>
              ) : null}
              {plant.plantName ? (
                <h3 className="mt-4 text-[1.5rem] font-semibold leading-tight tracking-tight text-white sm:mt-5 sm:text-[1.75rem] md:text-4xl lg:text-[2.5rem]">
                  {plant.plantName}
                </h3>
              ) : null}
              {(() => {
                if (plant.subHeading) {
                  return (
                    <div
                      className="mt-6 text-[11px] font-medium uppercase tracking-[0.32em] text-white/40 sm:mt-8 sm:text-xs sm:tracking-[0.35em] [&_p]:m-0"
                      dangerouslySetInnerHTML={{ __html: plant.subHeading }}
                    />
                  );
                }
                if (plant.comingSoon) {
                  return (
                    <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.32em] text-white/40 sm:mt-8 sm:text-xs sm:tracking-[0.35em]">
                      COMING SOON…
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </article>
      </Container>
    </div>
  );
}

function TimelinePlantImage({
  items,
  activeIdx,
  plantName,
}: {
  items: PlantTimelineDataItem[];
  activeIdx: number;
  plantName: string | null;
}) {
  const layers = items.map((item, i) => ({
    id: item.id,
    url: getOriginalMediaUrl(item.image),
    alt: item.image?.alternativeText || plantName || 'Manufacturing plant',
    width: item.image?.width ?? 1200,
    height: item.image?.height ?? 800,
    i,
  }));

  if (layers.length === 0) {
    return (
      <>
        <div className="relative min-h-[200px] w-full lg:hidden">
          <CmsImage
            src={null}
            alt={plantName || 'Manufacturing plant'}
            width={1200}
            height={800}
            className="h-auto w-full"
            sizes="100vw"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="absolute inset-0 hidden lg:block">
          <CmsImage
            src={null}
            alt={plantName || 'Manufacturing plant'}
            fill
            className="object-contain object-center"
            sizes="50vw"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/*
        Opacity must live on a wrapper — CmsImage applies its own
        isLoaded opacity via tailwind-merge, which would override
        layer.i === activeIdx opacity classes on the image itself.
      */}
      <div className="relative w-full lg:hidden">
        <div className="grid [&>*]:col-start-1 [&>*]:row-start-1">
          {layers.map((layer) => (
            <div
              key={layer.id}
              className={`transition-opacity duration-500 ${
                layer.i === activeIdx ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
              aria-hidden={layer.i !== activeIdx}
            >
              <CmsImage
                src={layer.url}
                alt={layer.alt}
                width={layer.width}
                height={layer.height}
                quality={90}
                className="h-auto w-full"
                sizes="100vw"
                priority={layer.i === 0}
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="absolute inset-0 hidden lg:block">
        {layers.map((layer) => (
          <div
            key={layer.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              layer.i === activeIdx ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
            }`}
            aria-hidden={layer.i !== activeIdx}
          >
            <CmsImage
              src={layer.url}
              alt={layer.alt}
              fill
              quality={90}
              className="object-contain object-center"
              sizes="50vw"
              priority={layer.i === 0}
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          </div>
        ))}
      </div>
    </>
  );
}

function ActivePlantSection({
  section,
  plant,
  showSectionHeading = true,
}: {
  section: PlantsSectionWithHeading;
  plant: PlantEntry;
  showSectionHeading?: boolean;
}) {
  const scale = plant.theScale?.[0];
  const capacity = plant.capacity?.[0];
  const timeline = plant.timeline?.[0];
  const timelineItems = timeline?.data ?? [];
  const [activeTimelineIdx, setActiveTimelineIdx] = useState(0);

  useEffect(() => {
    setActiveTimelineIdx(0);
  }, [plant.id, timelineItems.length]);

  return (
    <div className="bg-black text-white">
      {showSectionHeading && (section.heading || section.subHeading) ? (
        <Container>
          <header className="mb-8 text-center sm:mb-10">
            {section.heading ? (
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight text-white sm:text-[2rem] md:text-5xl lg:text-[3.25rem]">
                {section.heading}
              </h2>
            ) : null}
            {section.subHeading ? (
              <div
                className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-white/70 sm:mt-5 sm:text-base md:text-lg [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: section.subHeading }}
              />
            ) : null}
          </header>
        </Container>
      ) : null}

      <Container>
        <article className="overflow-hidden rounded-2xl border border-white/[0.08] bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.04)]">
          <div className="grid lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] lg:items-stretch">
            <div className="space-y-6 bg-black px-5 py-7 sm:space-y-7 sm:px-6 sm:py-9 md:space-y-8 md:px-12 md:py-12 lg:max-w-none">
              {plant.locationName ? (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <PinIcon />
                  <span className="font-medium tracking-wide">{plant.locationName}</span>
                </div>
              ) : null}

              {plant.plantName ? (
                <h3 className="text-[1.5rem] font-semibold leading-[1.15] tracking-tight text-white sm:text-[1.75rem] sm:leading-[1.1] md:text-4xl lg:text-[2.75rem]">
                  {plant.plantName}
                </h3>
              ) : null}

              {scale?.description ? (
                <div className="flex flex-col gap-3 border-b border-white/[0.06] pb-7 sm:gap-4 sm:pb-8 md:flex-row md:items-start md:gap-10 md:pb-9">
                  <div className="shrink-0 md:w-[min(11rem,32%)] md:pt-0.5">
                    <SectionLabel>{scale.heading || 'THE SCALE'}</SectionLabel>
                  </div>
                  <div
                    className="min-w-0 flex-1 text-sm leading-6 text-white/80 sm:text-[15px] sm:leading-7 md:text-base md:leading-8 [&_p]:m-0"
                    dangerouslySetInnerHTML={{ __html: scale.description }}
                  />
                </div>
              ) : null}

              {capacity?.stats && capacity.stats.length > 0 ? (
                <div className="space-y-5">
                  <SectionLabel>{capacity.heading || 'CAPACITY'}</SectionLabel>
                  <div className="grid gap-6 sm:grid-cols-2 sm:gap-10 md:gap-12">
                    {capacity.stats.map((c) => (
                      <div key={c.id} className="space-y-1.5">
                        <div className="text-sm font-medium capitalize text-white">
                          {c.statheading}
                        </div>
                        <div className="text-[1.5rem] font-semibold tracking-tight text-white sm:text-[1.65rem] md:text-[1.85rem]">
                          {c.statValue} {c.statSuffix}
                        </div>
                        <p className="text-sm leading-6 text-[#888888]">{c.statDescription}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {timelineItems.length > 0 ? (
                <Timeline
                  heading={timeline?.heading ?? ''}
                  items={timelineItems}
                  onActiveChange={setActiveTimelineIdx}
                />
              ) : null}
            </div>

            <div className="relative w-full bg-black lg:min-h-[min(520px,72vh)]">
              <TimelinePlantImage
                items={timelineItems}
                activeIdx={activeTimelineIdx}
                plantName={plant.plantName}
              />
            </div>
          </div>
        </article>
      </Container>
    </div>
  );
}

export function PlantSectionWithHeadingBlock({ section }: { section: PlantsSectionWithHeading }) {
  const plants = section.plants ?? [];

  return plants.map((plant, index) =>
    plant.comingSoon === true ? (
      <ComingSoonPlantSection key={plant.id} section={section} plant={plant} />
    ) : (
      <ActivePlantSection
        key={plant.id}
        section={section}
        plant={plant}
        showSectionHeading={index === 0}
      />
    ),
  );
}

export function PlantsVideoSectionBlock({ section }: { section: PlantsVideoSection }) {
  return (
    <div className="bg-black text-white">
      <VideoBlock
        title={section.heading ?? ''}
        descriptionHtml={section.subHeading ?? ''}
        backgroundImage={section.media}
        button={section.button}
      />
    </div>
  );
}
